import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { ToolbarButton, ToolbarLabel, ToolbarWrapper } from './RichTextToolbar.styles';

export const RichTextToolbar = ({
  editor,
  label,
  disabled = false,
}: {
  editor: Editor;
  label: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { isBoldActive, isItalicActive, isUnderlineActive } = useEditorState({
    editor,
    selector: ({ editor }) => {
      const isActive = (mark: string) => editor.isFocused && editor.isActive(mark);
      return {
        isBoldActive: isActive('bold'),
        isItalicActive: isActive('italic'),
        isUnderlineActive: isActive('underline'),
      };
    },
  });

  return (
    <ToolbarWrapper role="toolbar" aria-label={label}>
      {/* onMouseDown + preventDefault keeps the editor focused through the click, unlike onClick. */}
      <ToolbarButton
        disabled={disabled}
        aria-label={t('richText.bold')}
        aria-pressed={isBoldActive}
        data-active={isBoldActive}
        onMouseDown={(event) => {
          event.preventDefault();
          editor.chain().focus().toggleBold().run();
        }}
      >
        <ToolbarLabel $variant="bold">{t('richText.boldShort')}</ToolbarLabel>
      </ToolbarButton>
      <ToolbarButton
        disabled={disabled}
        aria-label={t('richText.italic')}
        aria-pressed={isItalicActive}
        data-active={isItalicActive}
        onMouseDown={(event) => {
          event.preventDefault();
          editor.chain().focus().toggleItalic().run();
        }}
      >
        <ToolbarLabel $variant="italic">{t('richText.italicShort')}</ToolbarLabel>
      </ToolbarButton>
      <ToolbarButton
        disabled={disabled}
        aria-label={t('richText.underline')}
        aria-pressed={isUnderlineActive}
        data-active={isUnderlineActive}
        onMouseDown={(event) => {
          event.preventDefault();
          editor.chain().focus().toggleUnderline().run();
        }}
      >
        <ToolbarLabel $variant="underline">{t('richText.underlineShort')}</ToolbarLabel>
      </ToolbarButton>
    </ToolbarWrapper>
  );
};
